import { fieldDecisionRecordActionSchema } from '@mio/shared';
import { useId, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { FieldChoiceList, FieldEvidencePicker } from './field-discovery-inputs';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import type { FieldPanelProps } from './field-types';
import type { FieldDecision, FieldOutcomeId } from '@mio/shared';

export function FieldDecisionForm({
  snapshot,
  busy,
  onAction,
  decision,
  onClose,
}: FieldPanelProps & {
  decision?: FieldDecision;
  onClose: () => void;
}) {
  const id = useId();
  const [expectedVersion] = useState(decision?.version);
  const [title, setTitle] = useState(decision?.title ?? '');
  const [description, setDescription] = useState(decision?.description ?? '');
  const [kind, setKind] = useState<FieldDecision['kind']>(decision?.kind ?? 'question');
  const [owner, setOwner] = useState(decision?.owner ?? 'jordan');
  const [impact, setImpact] = useState<FieldDecision['impact']>(decision?.impact ?? 'medium');
  const [acceptance, setAcceptance] = useState(decision?.acceptance.join('\n') ?? '');
  const [sourceIds, setSourceIds] = useState(decision?.sourceIds ?? []);
  const [outcomeIds, setOutcomeIds] = useState<FieldOutcomeId[]>(decision?.outcomeIds ?? []);
  const [error, setError] = useState('');
  return (
    <form
      className="field-object-form space-y-4"
      aria-label={decision ? 'Edit decision' : 'Record understanding'}
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = fieldDecisionRecordActionSchema.safeParse({
          type: 'decision.record',
          title,
          description,
          kind,
          owner,
          impact,
          sourceIds,
          outcomeIds,
          acceptance: acceptance
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
          ...(decision ? { decisionId: decision.id, expectedVersion } : {}),
        });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? 'Check this record and try again.');
          return;
        }
        setError('');
        void onAction(parsed.data).then((saved) => {
          if (saved) onClose();
        });
      }}
    >
      <div>
        <h3 className="text-base font-semibold">
          {decision ? 'Refine your understanding' : 'Record your understanding'}
        </h3>
        <p className="text-fg-muted mt-2 text-sm leading-6">
          {decision
            ? 'Changes create a new version and reopen owner confirmation. Earlier versions remain in the record.'
            : 'Capture a requirement, assumption, open question, or scope decision. Save it first, then ask the owner to confirm.'}
        </p>
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-title`}>
          Title
        </label>
        <input
          id={`${id}-title`}
          className={FIELD_INPUT}
          required
          maxLength={200}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      <div className="field-object-form-grid" data-field-tour="decision-type-owner">
        <div>
          <label className={FIELD_LABEL} htmlFor={`${id}-kind`}>
            Record type
          </label>
          <SelectField
            id={`${id}-kind`}
            value={kind}
            onValueChange={(value) => {
              if (
                value === 'requirement' ||
                value === 'assumption' ||
                value === 'question' ||
                value === 'scope'
              )
                setKind(value);
            }}
            options={[
              { value: 'question', label: 'Open question' },
              { value: 'assumption', label: 'Assumption' },
              { value: 'requirement', label: 'Requirement' },
              { value: 'scope', label: 'Scope decision' },
            ]}
          />
        </div>
        <div>
          <label className={FIELD_LABEL} htmlFor={`${id}-owner`}>
            Colleague who can confirm
          </label>
          <SelectField
            id={`${id}-owner`}
            value={owner}
            onValueChange={(value) => {
              const actor = snapshot.actors.find((item) => item.id === value);
              if (actor) setOwner(actor.id);
            }}
            options={snapshot.actors.map((actor) => ({
              value: actor.id,
              label: `${actor.name} · ${actor.role}`,
            }))}
          />
        </div>
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-description`}>
          Understanding, question, or proposed decision
        </label>
        <textarea
          id={`${id}-description`}
          className={FIELD_INPUT}
          required
          rows={5}
          maxLength={12000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-impact`}>
          Impact if unresolved or wrong
        </label>
        <SelectField
          id={`${id}-impact`}
          value={impact}
          onValueChange={(value) => {
            if (value === 'high' || value === 'medium' || value === 'low') setImpact(value);
          }}
          options={[
            { value: 'high', label: 'High · affects a release or control' },
            { value: 'medium', label: 'Medium · affects delivery choices' },
            { value: 'low', label: 'Low · local detail' },
          ]}
        />
      </div>
      <div data-field-tour="decision-acceptance">
        <label className={FIELD_LABEL} htmlFor={`${id}-acceptance`}>
          Acceptance examples (one per line, optional for questions)
        </label>
        <textarea
          id={`${id}-acceptance`}
          className={FIELD_INPUT}
          rows={3}
          maxLength={40000}
          value={acceptance}
          onChange={(event) => setAcceptance(event.target.value)}
        />
      </div>
      <FieldChoiceList
        label="Related outcomes"
        value={outcomeIds}
        max={3}
        options={
          snapshot.discovery?.outcomes.map((outcome) => ({
            id: outcome.id,
            label: outcome.title,
          })) ?? []
        }
        onChange={(values) =>
          setOutcomeIds(
            snapshot.discovery?.outcomes
              .filter((item) => values.includes(item.id))
              .map((item) => item.id) ?? [],
          )
        }
      />
      <FieldEvidencePicker
        snapshot={snapshot}
        value={sourceIds}
        onChange={setSourceIds}
        {...(decision ? { excludeId: decision.id } : {})}
      />
      {error ? (
        <p role="alert" className="text-danger text-sm">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          disabled={busy || !snapshot.enabled || !title.trim() || !description.trim()}
        >
          Save record
        </Button>
        <Button variant="ghost" type="button" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
